/**
 * Urgency Calculation Utilities
 * GTD-style prioritization based on due dates and action states
 * Used for Dashboard Sections (My Turn / Their Turn / Scheduled / Nurture)
 */

import type { Contact } from './types.js';

// ═══════════════════════════════════════════════════════════════════════════
// URGENCY TYPES
// ═══════════════════════════════════════════════════════════════════════════

// Urgency levels for "My Turn" contacts (where action is required)
export type UrgencyLevel = 'overdue' | 'today' | 'this_week' | 'later' | 'none';

// Urgency levels for "Their Turn" contacts (waiting for response)
export type WaitingUrgency = 'fresh' | 'stale' | 'follow_up';

// ═══════════════════════════════════════════════════════════════════════════
// URGENCY CALCULATIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Calculate urgency level based on due date
 * Used for contacts where action is required
 */
export function getUrgencyLevel(dueDate: Date | string | null | undefined): UrgencyLevel {
  if (!dueDate) return 'none';

  const due = new Date(dueDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const endOfWeek = new Date(today);
  endOfWeek.setDate(endOfWeek.getDate() + (7 - endOfWeek.getDay()));

  if (due < today) return 'overdue';
  if (due.getTime() === today.getTime()) return 'today';
  if (due <= endOfWeek) return 'this_week';
  return 'later';
}

/**
 * Calculate waiting urgency based on last contact date
 * Used for contacts where we're waiting for their response
 */
export function getWaitingUrgency(lastContactDate: Date | string | null | undefined): WaitingUrgency {
  if (!lastContactDate) return 'follow_up';

  const lastContact = new Date(lastContactDate);
  const today = new Date();
  const daysSince = Math.floor((today.getTime() - lastContact.getTime()) / (1000 * 60 * 60 * 24));

  if (daysSince < 3) return 'fresh';
  if (daysSince <= 7) return 'stale';
  return 'follow_up';
}

// ═══════════════════════════════════════════════════════════════════════════
// STYLING HELPERS (Tacit Dark Professional)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get Tailwind classes for urgency badge
 * Uses Tacit Dark Professional color system
 */
export function getUrgencyBadgeColor(urgency: UrgencyLevel | WaitingUrgency): string {
  const colorMap: Record<UrgencyLevel | WaitingUrgency, string> = {
    // My Turn urgencies
    overdue: 'bg-status-danger/10 text-status-danger border-status-danger/30',
    today: 'bg-status-warning/10 text-status-warning border-status-warning/30',
    this_week: 'bg-status-info/10 text-status-info border-status-info/30',
    later: 'bg-surface text-muted-foreground border-border',
    none: 'bg-surface text-muted-foreground border-border',
    // Their Turn urgencies
    fresh: 'bg-status-success/10 text-status-success border-status-success/30',
    stale: 'bg-status-warning/10 text-status-warning border-status-warning/30',
    follow_up: 'bg-status-danger/10 text-status-danger border-status-danger/30',
  };

  return colorMap[urgency] || colorMap.none;
}

/**
 * Get urgency label for display
 */
export function getUrgencyLabel(urgency: UrgencyLevel | WaitingUrgency): string {
  const labelMap: Record<UrgencyLevel | WaitingUrgency, string> = {
    overdue: 'Overdue',
    today: 'Today',
    this_week: 'This Week',
    later: 'Later',
    none: 'No date',
    fresh: 'Fresh (<3d)',
    stale: 'Stale (3-7d)',
    follow_up: 'Follow up (>7d)',
  };

  return labelMap[urgency] || 'Unknown';
}

/**
 * Get urgency indicator element
 * Returns pulse dot for visual urgency cues
 */
export function getUrgencyIndicator(urgency: UrgencyLevel | WaitingUrgency): {
  className: string;
  animated: boolean;
} {
  const indicatorMap: Record<UrgencyLevel | WaitingUrgency, { className: string; animated: boolean }> = {
    // My Turn - Red pulse for overdue
    overdue: { className: 'bg-status-danger', animated: true },
    today: { className: 'bg-status-warning', animated: false },
    this_week: { className: 'bg-status-info', animated: false },
    later: { className: 'bg-text-tertiary', animated: false },
    none: { className: 'bg-text-tertiary', animated: false },
    // Their Turn
    fresh: { className: 'bg-status-success', animated: false },
    stale: { className: 'bg-status-warning', animated: false },
    follow_up: { className: 'bg-status-danger', animated: true },
  };

  return indicatorMap[urgency] || indicatorMap.none;
}

// ═══════════════════════════════════════════════════════════════════════════
// SORTING & PRIORITIZATION
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Sort contacts by urgency (most urgent first)
 * Prioritizes overdue → today → this week → later
 */
export function sortByUrgency(contacts: Contact[]): Contact[] {
  const urgencyOrder: Record<UrgencyLevel | WaitingUrgency, number> = {
    overdue: 0,
    today: 1,
    this_week: 2,
    follow_up: 3,
    stale: 4,
    fresh: 5,
    later: 6,
    none: 7,
  };

  return [...contacts].sort((a, b) => {
    // Determine urgency for each contact
    let urgencyA: UrgencyLevel | WaitingUrgency;
    let urgencyB: UrgencyLevel | WaitingUrgency;

    if (a.action_state === 'waiting_response') {
      urgencyA = getWaitingUrgency(a.last_contact_date);
    } else {
      urgencyA = getUrgencyLevel(a.due_date);
    }

    if (b.action_state === 'waiting_response') {
      urgencyB = getWaitingUrgency(b.last_contact_date);
    } else {
      urgencyB = getUrgencyLevel(b.due_date);
    }

    const orderDiff = urgencyOrder[urgencyA] - urgencyOrder[urgencyB];
    
    // If same urgency, sort by date
    if (orderDiff === 0) {
      const dateA = a.due_date || a.last_contact_date || a.created_at;
      const dateB = b.due_date || b.last_contact_date || b.created_at;
      return new Date(dateA).getTime() - new Date(dateB).getTime();
    }

    return orderDiff;
  });
}

/**
 * Get urgency summary for a contact
 * Unified interface for urgency information
 */
export function getContactUrgency(contact: Contact): {
  level: UrgencyLevel | WaitingUrgency;
  label: string;
  badgeColor: string;
  indicator: { className: string; animated: boolean };
} {
  let level: UrgencyLevel | WaitingUrgency;

  if (contact.action_state === 'waiting_response') {
    level = getWaitingUrgency(contact.last_contact_date);
  } else {
    level = getUrgencyLevel(contact.due_date);
  }

  return {
    level,
    label: getUrgencyLabel(level),
    badgeColor: getUrgencyBadgeColor(level),
    indicator: getUrgencyIndicator(level),
  };
}
